import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { StatusDot, type StatusTone } from './StatusDot.js';

/** The ten component states every data surface must be able to show (spec section 22). */
export type ComponentState =
  | 'empty'
  | 'loading'
  | 'complete'
  | 'stale'
  | 'partial'
  | 'rate-limited'
  | 'disconnected'
  | 'unauthorized'
  | 'unsupported'
  | 'failed';

export const COMPONENT_STATES: readonly ComponentState[] = [
  'empty',
  'loading',
  'complete',
  'stale',
  'partial',
  'rate-limited',
  'disconnected',
  'unauthorized',
  'unsupported',
  'failed',
];

/** Exact user-facing labels. Rendered uppercase by the mono label convention. */
export const STATE_LABELS: Record<ComponentState, string> = {
  empty: 'Empty',
  loading: 'Loading',
  complete: 'Complete',
  stale: 'Stale',
  partial: 'Partial',
  'rate-limited': 'Rate limited',
  disconnected: 'Disconnected',
  unauthorized: 'Unauthorized',
  unsupported: 'Unsupported',
  failed: 'Failed',
};

export const STATE_TONES: Record<ComponentState, StatusTone> = {
  empty: 'dim',
  loading: 'info',
  complete: 'ok',
  stale: 'warn',
  partial: 'warn',
  'rate-limited': 'warn',
  // Design system section 8.5: open / critical / disconnected use --brand; dim is reserved for
  // closed / offline / inactive. Matches the rail dot and connection-health tone for the same state.
  disconnected: 'brand',
  unauthorized: 'brand',
  unsupported: 'dim',
  failed: 'brand',
};

export interface StateBadgeProps {
  state: ComponentState;
  /** Extra explanation after a middot, e.g. `people failed` or `retry in 18s`. Never a bare spinner. */
  detail?: ReactNode;
  className?: string;
}

/** Dot + exact state label, optionally followed by `· detail`. */
export function StateBadge({ state, detail, className }: StateBadgeProps) {
  const tone = STATE_TONES[state];
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)} data-state={state}>
      <StatusDot
        tone={tone}
        tintLabel={tone !== 'dim'}
        label={
          <>
            {STATE_LABELS[state]}
            {detail ? (
              <>
                <span className="text-dim"> · </span>
                <span className="text-mute">{detail}</span>
              </>
            ) : null}
          </>
        }
      />
    </span>
  );
}
